import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  CircularProgress,
  Alert,
  colors,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { dashboardRevenueData } from "../../../ApiService/ApiService";
import { BarChart } from "@mui/x-charts";

function Revenue({ schoolId, sessions, sessionsError, sessionsLoading }) {
  const [sessionId, setSessionId] = useState("");
  const [revenueData, setRevenueData] = useState(null);
  const [barDataYear, setBarDataYear] = useState({ xAxis: [], series: [] });
  const [barDataMonth, setBarDataMonth] = useState({ xAxis: [], series: [] });
  const [barDataClass, setBarDataClass] = useState({ xAxis: [], series: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (sessionId) {
      setLoading(true);
      setError(null);
      dashboardRevenueData(sessionId, schoolId)
        .then((response) => {
          const data = response.data.data;
          setRevenueData(data);

          // Prepare bar chart data for byYear
          const yearData = data.byYear;
          const yearLabels = yearData.map((item) => item.name);
          const yearValues = yearData.map((item) => item.data);
          setBarDataYear({
            xAxis: yearLabels,
            series: [
              {
                label: "Revenue by Year",
                data: yearValues,
                color: "green",
              },
            ],
          });

          // Prepare bar chart data for byMonth
          const monthData = data.byMonth;
          const monthLabels = Object.keys(monthData);
          const monthValues = monthLabels.map((month) =>
            monthData[month].reduce((sum, item) => sum + item.data, 0)
          );
          setBarDataMonth({
            xAxis: monthLabels,
            series: [
              {
                label: "Revenue by Month",
                data: monthValues,
                color: "green",
              },
            ],
          });

          // Prepare bar chart data for byClass
          const classData = data.byClass;
          const classLabels = Object.keys(classData);
          const classValues = Object.values(classData);
          setBarDataClass({
            xAxis: classLabels,
            series: [
              {
                label: "Revenue by Class",
                data: classValues,
                color: "green",
              },
            ],
          });

          setLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching revenue data:", error);
          setError("Error fetching revenue data");
          setLoading(false);
        });
    }
  }, [sessionId, schoolId]);

  useEffect(() => {
    if (sessions?.data?.length > 0) {
      setSessionId(sessions.data[0].id);
    }
  }, [sessions]);

  return (
    <Box
      sx={{
        p: 2,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <FormControl required size="small" sx={{ width: "25%", marginBottom: 2 }}>
        <InputLabel id="sessions">Sessions</InputLabel>
        <Select
          name="sessionId"
          label="Sessions"
          labelId="sessions"
          value={sessionId}
          onChange={(e) => setSessionId(e.target.value)}
          size="small"
          disabled={sessionsLoading || sessionsError}
        >
          {sessionsLoading ? (
            <MenuItem value="">
              <CircularProgress size={24} />
            </MenuItem>
          ) : sessionsError ? (
            <MenuItem value="">
              <Alert severity="error">Error fetching sessions</Alert>
            </MenuItem>
          ) : (
            sessions?.data?.map((currSession) => (
              <MenuItem key={currSession.id} value={currSession.id}>
                {currSession.name}
              </MenuItem>
            ))
          )}
        </Select>
      </FormControl>

      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <CircularProgress size={48} />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : revenueData ? (
        <>
          <Typography
            variant="h6"
            sx={{
              marginBottom: 5,
              marginTop: 2,
              fontWeight: "bold",
            }}
          >
            Total Year Revenue: Rs. {revenueData.total}
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: 2,
              overflowX: "auto",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ marginBottom: 1, fontWeight: "bold" }}
              >
                Revenue by Year
              </Typography>
              <BarChart
                xAxis={[{ scaleType: "band", data: barDataYear.xAxis }]}
                series={barDataYear.series}
                width={300}
                height={250}
              />
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ marginBottom: 1, fontWeight: "bold" }}
              >
                Revenue by Month
              </Typography>
              <BarChart
                xAxis={[{ scaleType: "band", data: barDataMonth.xAxis }]}
                series={barDataMonth.series}
                width={300}
                height={250}
              />
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ marginBottom: 1, fontWeight: "bold" }}
              >
                Revenue by Class
              </Typography>
              <BarChart
                xAxis={[{ scaleType: "band", data: barDataClass.xAxis }]}
                series={barDataClass.series}
                width={400}
                height={250}
              />
            </Box>
          </Box>
        </>
      ) : (
        <Typography variant="body1" sx={{ marginTop: 2 }}>
          Please select a session to view revenue data.
        </Typography>
      )}
    </Box>
  );
}

export default Revenue;
